import _ from 'lodash';
import { getRetailerOrders, getRetailerOrderFilters, downloadReports } from './api/retailers';
import { getTotalCountHeader } from './api/utils';
import { alertError } from './alert';
import { filterOrdersBySearch, filterOrdersByStates } from '../../utils/orderTableHelper';
import { OrderTypesAllTypes } from '../../constants/orderTypes';
import {
  DROPSHIP_ORDERS_LOADING,
  DROPSHIP_ORDERS_FILTERS,
  DROPSHIP_ORDERS_FILTER_DATA,
  DROPSHIP_ORDERS_COUNT,
  DROPSHIP_ORDERS_OFFSET,
  DROPSHIP_ORDERS_PER_PAGE,
  DROPSHIP_ORDERS_SEARCH,
  DROPSHIP_ORDERS_SORT,
  DROPSHIP_ORDERS_STATUS_COUNT,
  DROPSHIP_ORDERS_REPORT,
  DROPSHIP_ORDERS_REPORT_CLOSE,
  DROPSHIP_ORDERS_SUPPLIER_IDS,
  DROPSHIP_ORDERS_ORDER_TYPES,
} from '../constants/dropshipOrdersTypes';

export const setDropshipOrdersLoading = (value) => ({ type: DROPSHIP_ORDERS_LOADING, payload: value });
const setDropshipOrdersFilters = (filters) => ({ type: DROPSHIP_ORDERS_FILTERS, payload: filters });

const filterData = (data, title) => ({
  type: DROPSHIP_ORDERS_FILTER_DATA,
  payload: {
    data,
    title,
  },
});

const setDropshipOrdersCount = (count) => ({ type: DROPSHIP_ORDERS_COUNT, payload: { all: count } });

export const setDropshipOrdersOffset = (offset) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_OFFSET, payload: offset });
  dispatch(getDropshipOrders());
};

export const setDropshipOrdersPerPage = (perPage) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_PER_PAGE, payload: perPage });
  dispatch({ type: DROPSHIP_ORDERS_OFFSET, payload: 0 });
  dispatch(getDropshipOrders());
};

export const setDropshipOrdersSearchById = (search) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_SEARCH, payload: search });
  dispatch(getDropshipOrders());
};

export const setDropshipOrdersSort = (field) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_SORT, payload: field });
  dispatch(getDropshipOrders());
};

export const setDropshipOrdersSupplierIds = (supplierIds) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_SUPPLIER_IDS, payload: supplierIds });
  dispatch(getDropshipOrdersData());
};

export const setDropshipOrdersOrderTypes = (orderTypes) => (dispatch) => {
  dispatch({ type: DROPSHIP_ORDERS_ORDER_TYPES, payload: orderTypes });
  dispatch(getDropshipOrdersData());
};

export const getDropshipOrdersData = (title) => async (dispatch, getState) => {
  dispatch(setDropshipOrdersLoading(true));

  try {
    if (title) {
      dispatch({ type: DROPSHIP_ORDERS_OFFSET, payload: 0 });
      dispatch({ type: DROPSHIP_ORDERS_SEARCH, payload: '' });
    }

    const state = getState();
    const { retailerId } = state.role;
    const { startDate, endDate } = state.pageFilters.dateRange;
    const { activeCount, offset, perPage, search, sort, field, selectedSupplierIds, selectedOrderTypes } =
      state.dropshipOrders;

    const filter = {
      created_at: { $between: [startDate, endDate] },
    };

    if (selectedOrderTypes?.length < Object.keys(OrderTypesAllTypes).length) {
      const selectedTypes = selectedOrderTypes.flatMap((orderType) => OrderTypesAllTypes[orderType]);
      filter.type = {
        $in: selectedTypes,
      };
    }

    if (selectedSupplierIds && selectedSupplierIds.length > 0) {
      filter.supplier_id = { $in: selectedSupplierIds };
    }

    const ordersCountersPromise = getRetailerOrderFilters(retailerId, filter);

    const title_lower = title ? title.toLowerCase() : activeCount.toLowerCase();
    filterOrdersByStates(filter, title_lower);
    filterOrdersBySearch(filter, search);
    const ordersPromise = getRetailerOrders(retailerId, filter, perPage, offset, sort, field);

    const [ordersResponse, countersResponse] = await Promise.all([ordersPromise, ordersCountersPromise]);

    const newCount = getTotalCountHeader(ordersResponse);
    const { data } = countersResponse;
    const sum = Object.values(_.omit(data, 'types')).reduce((a, b) => a + b, 0);

    dispatch(filterData(ordersResponse.data, title ? title : activeCount));
    dispatch(setDropshipOrdersFilters(data));
    dispatch(setDropshipOrdersCount(sum));
    dispatch({ type: DROPSHIP_ORDERS_STATUS_COUNT, payload: newCount });
    dispatch(setDropshipOrdersLoading(false));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getDropshipOrders = (title) => async (dispatch, getState) => {
  dispatch(setDropshipOrdersLoading(true));

  try {
    if (title) {
      dispatch({ type: DROPSHIP_ORDERS_OFFSET, payload: 0 });
      dispatch({ type: DROPSHIP_ORDERS_SEARCH, payload: '' });
    }

    const state = getState();
    const { retailerId } = state.role;
    const { startDate, endDate } = state.pageFilters.dateRange;
    const { activeCount, offset, perPage, search, sort, field, selectedSupplierIds, selectedOrderTypes } =
      state.dropshipOrders;

    const filter = {
      created_at: { $between: [startDate, endDate] },
    };

    if (selectedOrderTypes?.length < Object.keys(OrderTypesAllTypes).length) {
      const selectedTypes = selectedOrderTypes.flatMap((orderType) => OrderTypesAllTypes[orderType]);
      filter.type = {
        $in: selectedTypes,
      };
    }

    if (selectedSupplierIds && selectedSupplierIds.length > 0) {
      filter.supplier_id = { $in: selectedSupplierIds };
    }

    const title_lower = title ? title.toLowerCase() : activeCount.toLowerCase();
    filterOrdersByStates(filter, title_lower);
    filterOrdersBySearch(filter, search);

    const response = await getRetailerOrders(retailerId, filter, perPage, offset, sort, field);
    const newCount = getTotalCountHeader(response);

    dispatch(filterData(response.data, title ? title : activeCount));
    dispatch({ type: DROPSHIP_ORDERS_STATUS_COUNT, payload: newCount });
    dispatch(setDropshipOrdersLoading(false));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getDropshipOrdersFilters = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const { retailerId } = state.role;
    const { startDate, endDate } = state.pageFilters.dateRange;
    const { selectedSupplierIds } = state.dropshipOrders;

    const filter = {
      created_at: { $between: [startDate, endDate] },
    };

    if (selectedSupplierIds && selectedSupplierIds.length > 0) {
      filter.supplier_ids = { $in: selectedSupplierIds };
    }

    const response = await getRetailerOrderFilters(retailerId, filter);
    const data = response.data;
    const sum = Object.values(_.omit(data, 'types')).reduce((a, b) => a + b, 0);

    dispatch(setDropshipOrdersFilters(data));
    dispatch(setDropshipOrdersCount(sum));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const dropshipOrdersReport = () => async (dispatch, getState) => {
  const { role, pageFilters, dropshipOrders } = getState();
  const { retailerId } = role;
  const { startDate, endDate } = pageFilters.dateRange;
  const { selectedSupplierIds } = dropshipOrders;

  try {
    const data = {
      type: 'ecommerce-orders',
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    };

    if (selectedSupplierIds?.length) {
      data.supplier_ids = selectedSupplierIds;
    }

    const response = await downloadReports(retailerId, data);

    if (response.data.success) {
      dispatch({ type: DROPSHIP_ORDERS_REPORT });
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const dropshipOrdersReportClose = () => ({ type: DROPSHIP_ORDERS_REPORT_CLOSE });
